import { create } from 'zustand';
import axios from 'axios';

/**
 * Supported integration provider types
 */
export type IntegrationProvider = 'salsify' | 'syndigo' | 'inriver' | 'akeneo' | 'plytix' | 'pimcore';

/**
 * Raw API response from backend
 */
interface IntegrationApiResponse {
  service: string;
  isConnected: boolean;
  isTokenValid: boolean;
  clientId: string | null;
}

/**
 * Simplified status for a single integration provider
 * - isConnected: fully connected with valid token
 * - needsReconnect: was connected but token is invalid
 */
export interface IntegrationProviderStatus {
  service: string;
  isConnected: boolean;
  needsReconnect: boolean;
  clientId: string | null;
}

/**
 * State for a provider including loading and error states
 */
interface ProviderState {
  status: IntegrationProviderStatus | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Integration store state
 */
interface IntegrationStoreState {
  providers: Record<IntegrationProvider, ProviderState>;
  
  // Actions
  fetchProviderStatus: (provider: IntegrationProvider, targetUrl: string) => Promise<void>;
  setProviderLoading: (provider: IntegrationProvider, isLoading: boolean) => void;
  setProviderStatus: (provider: IntegrationProvider, status: IntegrationProviderStatus | null) => void;
  setProviderError: (provider: IntegrationProvider, error: string | null) => void;
  
  // Selectors
  getProviderState: (provider: IntegrationProvider) => ProviderState;
  isAnyProviderConnected: () => boolean;
  getConnectedProviders: () => IntegrationProvider[];
  isInitialLoading: () => boolean;
}

const DEFAULT_PROVIDER_STATE: ProviderState = {
  status: null,
  isLoading: true, // Start as loading
  error: null
};

const createInitialProviders = (): Record<IntegrationProvider, ProviderState> => ({
  salsify: { ...DEFAULT_PROVIDER_STATE },
  syndigo: { ...DEFAULT_PROVIDER_STATE, isLoading: false }, // Not implemented yet
  inriver: { ...DEFAULT_PROVIDER_STATE, isLoading: false }, // Not implemented yet
  akeneo: { ...DEFAULT_PROVIDER_STATE, isLoading: false }, // Not implemented yet
  plytix: { ...DEFAULT_PROVIDER_STATE, isLoading: false }, // Not implemented yet
  pimcore: { ...DEFAULT_PROVIDER_STATE, isLoading: false } // Not implemented yet
});

/**
 * Zustand store for managing OAuth integration statuses.
 * Designed as a singleton to cache integration statuses across the app.
 */
export const useIntegrationStore = create<IntegrationStoreState>()((set, get) => ({
  providers: createInitialProviders(),

  fetchProviderStatus: async (provider: IntegrationProvider, targetUrl: string) => {
    const state = get();
    const currentProvider = state.providers[provider];
    
    // Skip if already loaded and has status
    if (!currentProvider.isLoading && currentProvider.status !== null) {
      return;
    }

    set((state) => ({
      providers: {
        ...state.providers,
        [provider]: {
          ...state.providers[provider],
          isLoading: true,
          error: null
        }
      }
    }));

    try {
      const response = await axios.get<IntegrationApiResponse>(`https://${targetUrl}/api/integration/status/${provider}`);
      const apiData = response.data;
      
      // Transform API response: derive isConnected and needsReconnect from isTokenValid
      const status: IntegrationProviderStatus = {
        service: apiData.service,
        isConnected: apiData.isConnected && apiData.isTokenValid,
        needsReconnect: apiData.isConnected && !apiData.isTokenValid,
        clientId: apiData.clientId
      };
      
      set((state) => ({
        providers: {
          ...state.providers,
          [provider]: {
            status,
            isLoading: false,
            error: null
          }
        }
      }));
    } catch (error) {
      console.error(`Failed to fetch ${provider} integration status:`, error);
      set((state) => ({
        providers: {
          ...state.providers,
          [provider]: {
            status: null,
            isLoading: false,
            error: `Failed to fetch ${provider} status`
          }
        }
      }));
    }
  },

  setProviderLoading: (provider: IntegrationProvider, isLoading: boolean) => {
    set((state) => ({
      providers: {
        ...state.providers,
        [provider]: {
          ...state.providers[provider],
          isLoading
        }
      }
    }));
  },

  setProviderStatus: (provider: IntegrationProvider, status: IntegrationProviderStatus | null) => {
    set((state) => ({
      providers: {
        ...state.providers,
        [provider]: {
          ...state.providers[provider],
          status,
          isLoading: false
        }
      }
    }));
  },

  setProviderError: (provider: IntegrationProvider, error: string | null) => {
    set((state) => ({
      providers: {
        ...state.providers,
        [provider]: {
          ...state.providers[provider],
          error
        }
      }
    }));
  },

  getProviderState: (provider: IntegrationProvider) => {
    return get().providers[provider];
  },

  isAnyProviderConnected: () => {
    const providers = get().providers;
    return Object.values(providers).some((p) => p.status?.isConnected);
  },

  getConnectedProviders: () => {
    const providers = get().providers;
    return (Object.keys(providers) as IntegrationProvider[]).filter(
      (key) => providers[key].status?.isConnected
    );
  },

  isInitialLoading: () => {
    // Only check providers that should be loaded (salsify for now)
    const providers = get().providers;
    return providers.salsify.isLoading;
  }
}));
